/**
 * TrackCountdownBanner — slim sticky bar at the top of `/` and `/pricing`
 * showing the live "N tracks until the £25 lifetime tier closes" scarcity
 * anchor. Reads live from `/api/public/categories-lite` so it never goes
 * stale, and hides itself once the milestone is reached.
 *
 * Dismissible per-user via localStorage — one dismissal lasts 24 hours so
 * the anchor keeps pressuring conversion but doesn't nag returning visitors.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { X, Zap } from "lucide-react";

const STORAGE_KEY = "stagvault_countdown_banner_dismissed_at";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const TRACK_TARGET = 100;

export function TrackCountdownBanner() {
  const [tracksShipped, setTracksShipped] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const at = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      return at && Date.now() - at < DISMISS_TTL_MS;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (dismissed) return;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/public/categories-lite`)
      .then((r) => {
        if (Array.isArray(r.data)) setTracksShipped(r.data.length);
      })
      .catch(() => {
        /* stay silent — banner won't render */
      });
  }, [dismissed]);

  if (dismissed || tracksShipped === null) return null;
  const remaining = TRACK_TARGET - tracksShipped;
  if (remaining <= 0) return null; // milestone reached

  function handleDismiss() {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      /* no-op */
    }
    setDismissed(true);
  }

  return (
    <div
      className="sticky top-0 z-40 bg-gradient-to-r from-purple-900/95 via-black to-amber-950/70 border-b border-amber-400/30 backdrop-blur-md"
      data-testid="track-countdown-banner"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 md:py-2.5 flex items-center gap-3">
        <Zap size={14} className="text-amber-300 shrink-0" strokeWidth={2.5} />
        <p className="flex-1 text-[11px] md:text-sm text-zinc-100 leading-tight">
          <span className="text-amber-300 font-bold">{remaining} track{remaining === 1 ? "" : "s"}</span>
          <span className="text-zinc-300"> until the £25 lifetime tier closes.</span>
          <span className="hidden md:inline text-zinc-500"> Shipping every Friday.</span>
          <Link
            to="/1000"
            className="ml-2 text-amber-200 hover:text-amber-100 underline underline-offset-2 font-semibold"
            data-testid="countdown-banner-cta"
          >
            Claim £25 →
          </Link>
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss countdown banner for 24 hours"
          className="text-zinc-500 hover:text-zinc-200 transition-colors shrink-0"
          data-testid="countdown-banner-dismiss"
        >
          <X size={16} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
